/**
 * Service layer for I18n.
 * This service centralizes the state, orchestration, or data access used by its feature area.
 */
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { UserLanguage } from '../services/repositories/api-repository';

/**
 * SERVICE: I18n
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly translate = inject(TranslateService);
  private readonly supportedLanguages: UserLanguage[] = ['en', 'fr'];
  private initPromise: Promise<void> | null = null;
  private initialized = false;
  private pendingLanguage: UserLanguage | null = null;

  /**
   * BUSINESS LOGIC: init
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  async init(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this.configureTranslations();

    return this.initPromise;
  }

  /**
   * BUSINESS LOGIC: useLanguage
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  useLanguage(language: UserLanguage | string | null | undefined): void {
    const normalizedLanguage = this.normalizeLanguage(language);
    this.pendingLanguage = normalizedLanguage;

    if (!this.initialized) {
      void this.initPromise?.then(() => this.loadLanguage(normalizedLanguage));
      return;
    }

    void this.loadLanguage(normalizedLanguage);
  }

  /**
   * BUSINESS LOGIC: currentLanguage
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  currentLanguage(): UserLanguage {
    const activeLanguage = this.translate.currentLang || this.pendingLanguage;

    return activeLanguage === 'fr' ? 'fr' : 'en';
  }

  /**
   * BUSINESS LOGIC: configureTranslations
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private async configureTranslations(): Promise<void> {
    this.translate.addLangs(this.supportedLanguages);
    this.translate.setDefaultLang('en');
    await firstValueFrom(this.translate.setFallbackLang('en'));
    const storedLanguage = localStorage.getItem('preferredLanguage')?.toLowerCase();
    const initialLanguage = this.pendingLanguage ?? (storedLanguage === 'fr' ? 'fr' : 'en');
    await this.loadLanguage(initialLanguage);
    this.initialized = true;
  }

  /**
   * BUSINESS LOGIC: loadLanguage
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private async loadLanguage(language: UserLanguage): Promise<void> {
    await firstValueFrom(this.translate.use(language));
  }

  /**
   * BUSINESS LOGIC: normalizeLanguage
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeLanguage(language: UserLanguage | string | null | undefined): UserLanguage {
    return String(language ?? '').toLowerCase() === 'fr' ? 'fr' : 'en';
  }
}
